export interface TurnstileOptions {
  siteKey?: string;
  containerId?: string;
  scriptTimeoutMs?: number;
  tokenTimeoutMs?: number;
}

export interface TurnstileTokenRequest {
  timeoutMs?: number;
}

export interface Turnstile {
  getToken(request?: TurnstileTokenRequest): Promise<string | null>;
  preload(): Promise<boolean>;
  destroy(): void;
}

type TurnstileWidgetId = string;

interface TurnstileRenderParameters {
  sitekey: string;
  action?: string;
  appearance?: "always" | "execute" | "interaction-only";
  execution?: "render" | "execute";
  "refresh-expired"?: "auto" | "manual" | "never";
  callback?: (token: string) => void;
  "error-callback"?: (code?: string) => boolean | void;
  "timeout-callback"?: () => void;
  "unsupported-callback"?: () => boolean | void;
}

interface TurnstileApi {
  render(
    container: HTMLElement,
    parameters: TurnstileRenderParameters,
  ): TurnstileWidgetId;
  execute(container: HTMLElement): void;
  reset(widgetId?: TurnstileWidgetId): void;
  remove(widgetId?: TurnstileWidgetId): void;
}

type TurnstileScope = Record<string, unknown> & { turnstile?: TurnstileApi };

const SCRIPT_ELEMENT_ID = "cf-turnstile-script";
const ONLOAD_CALLBACK_NAME = "__nexorTurnstileOnload";
const SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK_NAME}`;
const DEFAULT_SCRIPT_TIMEOUT_MS = 8000;
const DEFAULT_TOKEN_TIMEOUT_MS = 20000;
const DEFAULT_ACTION = "webchat";

const CONTAINER_MARKER_ATTRIBUTE = "data-nexor-turnstile";
const CONTAINER_STYLE_ELEMENT_ID = "nexor-turnstile-style";
const CLICKABLE_CHALLENGE_IFRAME_CSS = `[${CONTAINER_MARKER_ATTRIBUTE}] iframe{pointer-events:auto}`;
const CENTERED_ABOVE_EVERYTHING: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: "2147483647",
  pointerEvents: "none",
};

const NOOP_TURNSTILE: Turnstile = {
  getToken: () => Promise.resolve(null),
  preload: () => Promise.resolve(false),
  destroy: () => {},
};

function hasDom(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function swallowingErrors<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

function turnstileScope(): TurnstileScope {
  return window as unknown as TurnstileScope;
}

function loadedTurnstileApi(): TurnstileApi | null {
  return turnstileScope().turnstile ?? null;
}

let scriptLoad: Promise<TurnstileApi | null> | null = null;

function forgetFailedScript(): void {
  document.getElementById(SCRIPT_ELEMENT_ID)?.remove();
  scriptLoad = null;
}

function loadTurnstileApi(timeoutMs: number): Promise<TurnstileApi | null> {
  const alreadyLoaded = loadedTurnstileApi();
  if (alreadyLoaded) return Promise.resolve(alreadyLoaded);
  if (scriptLoad) return scriptLoad;

  scriptLoad = new Promise<TurnstileApi | null>((resolve) => {
    let settled = false;
    const finish = (api: TurnstileApi | null) => {
      if (settled) return;
      settled = true;
      if (!api) forgetFailedScript();
      resolve(api);
    };

    turnstileScope()[ONLOAD_CALLBACK_NAME] = () => finish(loadedTurnstileApi());

    const script = document.createElement("script");
    script.id = SCRIPT_ELEMENT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("error", () => finish(null), { once: true });
    document.head.appendChild(script);

    setTimeout(() => finish(null), timeoutMs);
  });

  return scriptLoad;
}

function keepChallengeIframeClickable(): void {
  if (document.getElementById(CONTAINER_STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = CONTAINER_STYLE_ELEMENT_ID;
  style.textContent = CLICKABLE_CHALLENGE_IFRAME_CSS;
  document.head.appendChild(style);
}

let containerSequence = 0;

function nextContainerId(): string {
  containerSequence += 1;
  return `nexor-turnstile-${containerSequence}`;
}

interface WidgetState {
  id: TurnstileWidgetId | null;
  container: HTMLElement | null;
  containerIsOurs: boolean;
  needsResetBeforeExecute: boolean;
}

function emptyWidgetState(): WidgetState {
  return {
    id: null,
    container: null,
    containerIsOurs: false,
    needsResetBeforeExecute: false,
  };
}

/**
 * Creates an isolated invisible Turnstile widget that mints a fresh, single-use
 * token per call. Safe to import and call in Node: without a DOM every method
 * is a no-op and `getToken()` resolves to `null`.
 */
export function createTurnstile(options: TurnstileOptions): Turnstile {
  const siteKey = options?.siteKey?.trim() ?? "";
  if (!hasDom() || !siteKey) return NOOP_TURNSTILE;

  const containerId = options.containerId ?? nextContainerId();
  const scriptTimeoutMs = options.scriptTimeoutMs ?? DEFAULT_SCRIPT_TIMEOUT_MS;
  const defaultTokenTimeoutMs =
    options.tokenTimeoutMs ?? DEFAULT_TOKEN_TIMEOUT_MS;

  let widget = emptyWidgetState();
  let pendingTokenResolver: ((token: string | null) => void) | null = null;
  let tokenQueue: Promise<string | null> = Promise.resolve(null);
  let tokenGeneration = 0;
  let destroyed = false;

  function settlePendingToken(token: string | null): void {
    const resolve = pendingTokenResolver;
    pendingTokenResolver = null;
    if (resolve) resolve(token || null);
  }

  function currentGenerationSettler(generation: number) {
    return (token: string | null) => {
      if (generation !== tokenGeneration) return;
      settlePendingToken(token);
    };
  }

  function ensureContainer(): HTMLElement {
    if (widget.container?.isConnected) return widget.container;

    const adopted = document.getElementById(containerId);
    if (adopted) {
      adopted.setAttribute(CONTAINER_MARKER_ATTRIBUTE, "");
      keepChallengeIframeClickable();
      widget.container = adopted;
      widget.containerIsOurs = false;
      return adopted;
    }

    const created = document.createElement("div");
    created.id = containerId;
    created.setAttribute(CONTAINER_MARKER_ATTRIBUTE, "");
    Object.assign(created.style, CENTERED_ABOVE_EVERYTHING);
    keepChallengeIframeClickable();
    document.body.appendChild(created);

    widget.container = created;
    widget.containerIsOurs = true;
    return created;
  }

  function ensureWidget(api: TurnstileApi): TurnstileWidgetId {
    if (widget.id != null && widget.container?.isConnected) return widget.id;

    if (widget.id != null) {
      const orphanId = widget.id;
      swallowingErrors(() => api.remove(orphanId));
      widget.id = null;
    }

    widget.id = api.render(ensureContainer(), {
      sitekey: siteKey,
      action: DEFAULT_ACTION,
      appearance: "interaction-only",
      execution: "execute",
      "refresh-expired": "manual",
      callback: settlePendingToken,
      "error-callback": () => {
        settlePendingToken(null);
        return true;
      },
      "timeout-callback": () => settlePendingToken(null),
      "unsupported-callback": () => {
        settlePendingToken(null);
        return true;
      },
    });

    return widget.id;
  }

  function resetThenExecute(api: TurnstileApi, id: TurnstileWidgetId): void {
    if (widget.needsResetBeforeExecute) api.reset(id);
    widget.needsResetBeforeExecute = true;
    api.execute(ensureContainer());
  }

  async function requestToken(timeoutMs: number): Promise<string | null> {
    const api = await loadTurnstileApi(scriptTimeoutMs);
    if (!api) return null;

    const id = swallowingErrors(() => ensureWidget(api));
    if (id == null) return null;

    const generation = ++tokenGeneration;
    const settleThisGeneration = currentGenerationSettler(generation);

    return new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => {
        swallowingErrors(() => api.reset(id));
        settleThisGeneration(null);
      }, timeoutMs);

      pendingTokenResolver = (token) => {
        clearTimeout(timer);
        resolve(token);
      };

      try {
        resetThenExecute(api, id);
      } catch {
        settleThisGeneration(null);
      }
    });
  }

  return {
    getToken(request: TurnstileTokenRequest = {}) {
      if (destroyed) return Promise.resolve(null);
      const timeoutMs = request.timeoutMs ?? defaultTokenTimeoutMs;
      tokenQueue = tokenQueue.then(() =>
        requestToken(timeoutMs).catch(() => null),
      );
      return tokenQueue;
    },

    preload() {
      if (destroyed) return Promise.resolve(false);
      return loadTurnstileApi(scriptTimeoutMs).then(
        (api) =>
          !destroyed && !!api && swallowingErrors(() => ensureWidget(api)) != null,
      );
    },

    destroy() {
      destroyed = true;
      tokenGeneration += 1;
      settlePendingToken(null);
      if (widget.id != null) {
        const doomedId = widget.id;
        swallowingErrors(() => loadedTurnstileApi()?.remove(doomedId));
      }
      if (widget.containerIsOurs) widget.container?.remove();
      widget = emptyWidgetState();
    },
  };
}
